import { useState } from 'react';
import SoundPicker from '../../Common/SoundPicker/SoundPicker.jsx';
import Waveform from '../../Common/Waveform.jsx';
import useClipLookup from '../../Common/useClipLookup.js';
import { Button } from '../../Common/DesignComponents.jsx';
import formatClipTitle from '../../../utils/formatClipTitle.js';

export function AnsweringUI({
  assignedPrompt,
  hand = [],
  onAudition,
  onSubmitAnswer,
  onSkipPrompt,
  isSubmitted = false
}) {
  const [selectedClips, setSelectedClips] = useState([]);
  const lookupClip = useClipLookup();

  const promptText = assignedPrompt?.text || 'No prompt assigned';
  const authorName = assignedPrompt?.authorName || assignedPrompt?.author || 'Mira';

  const handleSelectClip = (clip) => {
    if (isSubmitted) return;
    const exists = selectedClips.find(c => c.id === clip.id);
    if (exists) {
      setSelectedClips(selectedClips.filter(c => c.id !== clip.id));
      return;
    }

    if (selectedClips.length < 2) {
      setSelectedClips([...selectedClips, clip]);
    } else {
      setSelectedClips([selectedClips[1], clip]);
    }
  };

  const handleClearSlot = (index) => {
    if (isSubmitted) return;
    if (index < selectedClips.length) {
      const next = [...selectedClips];
      next.splice(index, 1);
      setSelectedClips(next);
    }
  };

  const handleAudition = (clip) => {
    if (onAudition) onAudition(clip);
  };

  const handleSubmit = () => {
    if (selectedClips.length === 2 && onSubmitAnswer && !isSubmitted) {
      onSubmitAnswer({
        clipIds: selectedClips.map(c => c.id),
        clips: selectedClips
      });
    }
  };

  const ready = selectedClips.length === 2;

  return (
    <div
      className="answering-ui-container"
      data-testid="answering-ui"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      {/* Header & Prompt Display matching Main.dc.html */}
      <div style={{ padding: '4px 0 0', flex: 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '10.5px', letterSpacing: '0.18em', textTransform: 'uppercase', color: '#6E7982' }}>
            {authorName} wrote this for you
          </div>
          {onSkipPrompt && (
            <Button
              variant="ghost"
              size="sm"
              data-testid="skip-prompt-btn"
              onClick={onSkipPrompt}
              disabled={isSubmitted}
              style={{ minHeight: '44px', minWidth: '44px' }}
            >
              ⏭️ Skip Prompt
            </Button>
          )}
        </div>
        <div
          data-testid="assigned-prompt-display"
          style={{
            fontSize: '20px',
            lineHeight: '1.28',
            fontWeight: 700,
            letterSpacing: '-0.012em',
            color: '#EDEFF2'
          }}
        >
          "{promptText}"
        </div>
      </div>

      {/* Ordering Slots A & B matching Main.dc.html */}
      <div style={{ display: 'flex', gap: '10px', flex: 'none' }}>
        {[0, 1].map((i) => {
          const clip = selectedClips[i];
          const isSlotFilled = Boolean(clip);
          const color = clip?.color || (i === 0 ? '#3ACFCF' : '#E0A458');
          const durationText = clip?.dur || (typeof clip?.duration === 'number' ? `${clip.duration.toFixed(1)}s` : '2.4s');

          return (
            <div
              key={`slot-${i}`}
              onClick={() => isSlotFilled && handleClearSlot(i)}
              data-testid={`answer-slot-${i}`}
              style={{
                flex: '1 1 0',
                height: '76px',
                minHeight: '76px',
                borderRadius: '12px',
                border: isSlotFilled ? `1px solid ${color}` : '1px dashed #20262D',
                background: isSlotFilled ? 'rgba(255,255,255,0.03)' : 'transparent',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: '5px',
                padding: '0 12px',
                cursor: isSubmitted ? 'default' : (isSlotFilled ? 'pointer' : 'default'),
                userSelect: 'none',
                boxSizing: 'border-box'
              }}
            >
              {isSlotFilled ? (
                <>
                  <Waveform
                    peaks={clip.peaks || null}
                    seed={clip.id || clip.label}
                    bars={34}
                    height={26}
                    color={color}
                    dimColor="#262D34"
                    showPlayhead={false}
                  />
                  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px', minWidth: 0 }}>
                    <span
                      title={formatClipTitle(clip, 100)}
                      style={{
                        fontSize: '11.5px',
                        fontWeight: 600,
                        color: '#C6CDD4',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        minWidth: 0,
                        flex: '1 1 0',
                      }}
                    >
                      {formatClipTitle(clip)}
                    </span>
                    <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: '10px', color: '#6E7982', flex: 'none' }}>
                      {durationText}
                    </span>
                  </div>
                </>
              ) : (
                <div style={{ textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace", fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: '#6E7982' }}>
                  {i === 0 ? 'Sound A' : 'Sound B'}
                </div>
              )}
            </div>
          );
        })}
      </div>


      {/* SoundPicker Component */}
      <SoundPicker
        hand={hand}
        maxPicks={2}
        selectedClips={selectedClips}
        onSelectClip={handleSelectClip}
        onAuditionClip={handleAudition}
        onConfirmSelection={handleSubmit}
        disabled={isSubmitted}
        showSlotCards={false}
        showHeadphones={false}
      />

      {/* Submit CTA Section matching Main.dc.html */}
      <div style={{ flex: 'none', padding: '14px 0 8px', borderTop: '1px solid #1A1F25', background: '#08090B' }}>
        <button
          type="button"
          data-testid="submit-answer-btn"
          onClick={handleSubmit}
          disabled={!ready || isSubmitted}
          style={{
            width: '100%',
            height: '56px',
            minHeight: '56px',
            borderRadius: '14px',
            background: isSubmitted ? 'rgba(143,199,160,0.10)' : (ready ? '#3ACFCF' : '#0E1116'),
            border: isSubmitted ? '1px solid rgba(143,199,160,0.35)' : (ready ? '1px solid #3ACFCF' : '1px solid #1C2229'),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            cursor: (ready && !isSubmitted) ? 'pointer' : 'not-allowed',
            outline: 'none'
          }}
        >
          {isSubmitted && (
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M3.5 8.4l3 3 6-6.8" stroke="#8FC7A0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
          <span style={{ fontSize: '16px', fontWeight: 700, letterSpacing: '-0.005em', color: isSubmitted ? '#8FC7A0' : (ready ? '#08090B' : '#6E7982') }}>
            {isSubmitted ? 'Locked in' : (ready ? 'Send both sounds' : 'Pick two sounds')}
          </span>
        </button>
        <div style={{ textAlign: 'center', marginTop: '11px', fontFamily: "'IBM Plex Mono', monospace", fontSize: '10.5px', letterSpacing: '0.08em', color: '#6E7982' }}>
          {isSubmitted ? 'The host plays them out loud — headphones down' : 'Tap the pad to audition privately'}
        </div>
      </div>
    </div>
  );
}

export default AnsweringUI;
